import React, { useEffect, useMemo, useRef, useState } from "react";
import { ListProductUseCase } from "../../../Usecase/Products/list-product-usecase";
import { useToast } from "../../../Hooks/ToasContext";
import {
  IProductEntity,
  IMaskCodeDetails,
  TProductStoreType,
  IMaskOption,
  IGetMaskOptionsResponse,
  IGetVariantsByProductResponse,
  INuvemShopVariationMaskSelection,
} from "../../../Services/api/products/products.interface";
import { IPricePoliciesEntity } from "../../../Services/api/price-policies/price-policies.interface";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import { CreateBoxesUseCase } from "../../../Usecase/Boxes/create-boxes-usecase";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import * as Yup from "yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ICreateBoxPayload } from "../../../Services/api/boxes/boxes.interface";
import { IOptionsProps } from "../../../Components/Form/Select/select.component";
import { ListServiceInfoOrdersUseCase } from "../../../Usecase/ServicesInfo/list-service-info-usecase";
import { CreateProductsServiceOrdersUseCase } from "../../../Usecase/ProductsService/create-products-service-usecase";
import {
  IProductServiceEntity,
  IProductsServicePayload,
} from "../../../Services/api/products-services/products-services-interface";
import { CreateProductUseCase } from "../../../Usecase/Products/create-product-usecase";
import { ListCubagesUseCase } from "../../../Usecase/Cubages/list-cubages-usecase";
import { ICubageEntity } from "../../../Services/api/cubages/cubages.interface";
import { ListProductsServiceUseCase } from "../../../Usecase/ProductsService/list-products-service-usecase";
import { ShowProductUseCase } from "../../../Usecase/Products/show-product-usecase";
import { UpdateProductUseCase } from "../../../Usecase/Products/update-product-usecase";
import {
  ICubageVariantAssociated,
  ICubageVariantEntity,
} from "../../../Services/api/cubages-variants/cubages-variants.interface";
import { normalize } from "path";
import {
  ICategoryEntity,
  IListCategoryPayload,
} from "../../../Services/api/categories/categories.interface";
import { ListCategoriesUseCase } from "../../../Usecase/Categories/list-categories-usecase";
import { FindKeyWithPrefix } from "../../../utils/findKeyWithPrefix";
import { removeNonNumeric } from "../../../utils/removeNonNumeric";
import { brandMockOptions } from "../../../Config/shared";
import { useSelectPricePolicies } from "../../../Components/Modals/variants/select-price-policies";
import { GetMaskProductOptionsUseCase } from "../../../Usecase/Products/get-mask-product-options-usecase";
import { GetVariantsByProductUseCase } from "../../../Usecase/Products/get-variants-by-product-usecase";
import { TStoreType } from "../../../Services/api/service-orders/service-order.interface";
import { PRODUCTS_LIST_PATH } from "../../../utils/products-list-url";
import { UpdateProductActivationUseCase } from "../../../Usecase/Products/update-product-activation-usecase";

function dedupeCubageVariants(
  items: ICubageVariantAssociated[],
): ICubageVariantAssociated[] {
  const unique = new Map<string, ICubageVariantAssociated>();

  items.forEach((item) => {
    const variantId = item.cubageVariantId || item.cubageVariant?.id || item.id;
    if (variantId && !unique.has(variantId)) {
      unique.set(variantId, item);
    }
  });

  return Array.from(unique.values());
}

const hasPositiveNuvemShopPrice = (value?: string | number | null): boolean => {
  const numericValue = String(value ?? "").replace(/\D/g, "");

  return /[1-9]/.test(numericValue);
};

interface IUseCreateProductsProps {
  createProductUseCase: CreateProductUseCase;
  listCubagesUseCase: ListCubagesUseCase;
  listProductsServiceUseCase: ListProductsServiceUseCase;
  listServiceInfoOrdersUseCase: ListServiceInfoOrdersUseCase;
  showProductUseCase: ShowProductUseCase;
  updateProductUseCase: UpdateProductUseCase;
  listCategoriesUseCase: ListCategoriesUseCase;
  getMaskProductOptionsUseCase: GetMaskProductOptionsUseCase;
  getVariantsByProductUseCase: GetVariantsByProductUseCase;
  updateProductActivationUseCase: UpdateProductActivationUseCase;
}

export interface IProductProps {
  name: string;
  code: string;
  ncm: string;
  personalized: boolean;
  createOnNuvemShop: boolean;
  createOnNuvemShopB2B: boolean;
  createOnNuvemShopB2C: boolean;
  b2bNuvemShopPrice?: string;
  b2cNuvemShopPrice?: string;
  nsStore: TProductStoreType;
  // createOnOmie: boolean;
  createOnb2b: boolean;
  createOnb2c: boolean;
  price: string;
  promotionalPrice: string;
  description: string;
  billingType: "service" | "sales" | "resale";
  service: IOptionsProps;
  [key: `cubage-${string}-variant-${string}`]: boolean;
  [key: `switch-${string}`]: boolean;
  masterCode?: string;
  artLink?: string;
  maskCode?: string;
  internalName?: string;
  pricePolicies?: any[];
  omieRawMaterialCode?: string;
  isResale?: boolean;
  isMultiVariants?: boolean;
  b2bNuvemShopProductId?: string;
  b2cNuvemShopProductId?: string;
  loadNuvemShopVariationsB2B?: boolean;
  loadNuvemShopVariationsB2C?: boolean;
  productMultiVariants?: {
    variantId: string;
    code: string;
    maskCodeDetails: IMaskCodeDetails;
  }[];
}

export const billingOptions = [
  {
    value: "sales",
    label: "Faturar como venda",
  },
  {
    value: "service",
    label: "Faturar como serviço",
  },
  // {
  //   "value": "resale",
  //   "label": "Produto de revenda"
  // }
];

const storeTypeOptions = [
  { label: "Cadastrar na EMPO - B2B", value: "createOnNSEmpo" },
  { label: "Cadastrar na EMPO - B2C", value: "createOnNSPapello" },
];

const CreateBoxSchemaValidation = Yup.object().shape({
  name: Yup.string().required("Nome é obrigatório"),
  description: Yup.string().required("Descrição é obrigatório"),
  tributaryRegime: Yup.string().required("Regime tributário é obrigatório"),
  lcCode: Yup.string().required("Código LC é obrigatório"),
  nbsCode: Yup.string().required("Código NBS é obrigatório"),
  unitaryPrice: Yup.string().required("Preço unitário é obrigatório"),
  municipalityCode: Yup.string().required("Código Municipal é obrigatório"),
});

interface IServicesProps {
  value?: string;
  label?: string;
  name: string;
  description: string;
  tributaryRegime: string;
  lcCode: string;
  nbsCode: string;
  unitaryPrice: string;
  municipalityCode: string;
}

export function useCreateProducts({
  createProductUseCase,
  listCubagesUseCase,
  listProductsServiceUseCase,
  listServiceInfoOrdersUseCase,
  showProductUseCase,
  updateProductUseCase,
  listCategoriesUseCase,
  getMaskProductOptionsUseCase,
  getVariantsByProductUseCase,
  updateProductActivationUseCase,
}: IUseCreateProductsProps) {
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [categories, setCategories] = useState<IOptionsProps[]>([]);
  // const [categories,setCategories] = useState<ICategoryEntity[]>([])
  // const [categoriesSelected,setCategoriesSelected] = useState<ICategoryEntity[]>([])
  const [categoriesSelected, setCategoriesSelected] = useState<IOptionsProps[]>(
    [],
  );
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [showCubageModal, setShowCubageModal] = useState(false);
  const [showInternalCode, setShowInternalCode] = useState(false);
  const [showVariationsModal, setShowVariationsModal] = useState(false);
  const [maskSchema, setMaskSchema] = useState<IMaskCodeDetails>({});
  const [variations, setVariations] = useState<
    Array<{
      variationType: string;
      values: string[];
      howManySteps: string;
    }>
  >([]);
  const [cubagesList, setCubagesList] = useState<ICubageEntity[]>([]);
  const [productsServicesList, setProductsServicesList] = useState<
    IProductServiceEntity[]
  >([]);
  const [productsServicesOptions, setProductsServicesOptions] = useState<
    IOptionsProps[]
  >([]);
  const [tributaryRegimeOptions, setTributaryRegimeOptions] =
    useState<IOptionsProps[]>();
  const [lcCode, setLcCode] = useState<IOptionsProps[]>();
  const [nbsCode, setNbsCode] = useState<IOptionsProps[]>();
  const [data, setData] = useState<IProductEntity>();
  const [cubagesSelected, setCubagesSelected] = useState<
    ICubageVariantAssociated[]
  >([]);
  const [maskOptions, setMaskOptions] = useState<IGetMaskOptionsResponse>({
    types: [],
    families: [],
    volumetries: [],
    materials: [],
    complements: [],
    versions: [],
    brands: [],
  });
  const [maskOptionsLoaded, setMaskOptionsLoaded] = useState(false);

  // const [pricePolicies, setPricePolicies] = useState<Array<{
  //     id: string;
  //     minQuantity: number;
  //     discountPercentage: number;
  // }>>([])

  const [initialSelectedPolicies, setInitialSelectedPolicies] = useState<
    IPricePoliciesEntity[]
  >([]);
  const [showNuvemShopVariationsModal, setShowNuvemShopVariationsModal] =
    useState(false);
  const [nuvemShopVariationsModalChannel, setNuvemShopVariationsModalChannel] =
    useState<"B2B" | "B2C" | null>(null);
  const [nuvemShopVariantsB2B, setNuvemShopVariantsB2B] = useState<
    IGetVariantsByProductResponse[] | null
  >(null);
  const [nuvemShopVariantsB2C, setNuvemShopVariantsB2C] = useState<
    IGetVariantsByProductResponse[] | null
  >(null);
  const [nuvemShopVariationsLoading, setNuvemShopVariationsLoading] =
    useState<TStoreType | null>(null);
  const [
    nuvemShopVariationMaskSelections,
    setNuvemShopVariationMaskSelections,
  ] = useState<{
    B2B: INuvemShopVariationMaskSelection[];
    B2C: INuvemShopVariationMaskSelection[];
  }>({ B2B: [], B2C: [] });

  const {
    isOpen: showPricePoliciesModal,
    selectedPolicies,
    openModal: openPricePoliciesModal,
    closeModal: closePricePoliciesModal,
    handleSelect: handleSelectPricePolicies,
    removePolicy,
  } = useSelectPricePolicies({
    multiple: true,
    initialSelected: initialSelectedPolicies,
    onSelect: (policies: any) => console.log("Selecionadas:", policies),
  });

  const params = useParams();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productsListUrl = searchParams.get('return') || PRODUCTS_LIST_PATH;

  const {
    handleSubmit,
    register,
    control,
    formState,
    watch,
    setValue,
    unregister,
    getValues,
  } = useForm<IProductProps>({
    // resolver: yupResolver(CreateBoxSchemaValidation),
    defaultValues: {
      nsStore: undefined,
      name: "",
      description: "",
      price: "",
      code: "",
      ncm: "",
      promotionalPrice: "",
      personalized: false,
      createOnNuvemShopB2B: false,
      createOnNuvemShopB2C: false,
      b2bNuvemShopPrice: "",
      b2cNuvemShopPrice: "",
      billingType: "sales",
      omieRawMaterialCode: "",
      isResale: false,
      isMultiVariants: false,
      b2bNuvemShopProductId: "",
      b2cNuvemShopProductId: "",
      loadNuvemShopVariationsB2B: false,
      loadNuvemShopVariationsB2C: false,
    },
  });

  const nsStoreValue = watch("nsStore");

  const billingTypeValue = watch("billingType");

  const { errors } = formState;

  const { showToast } = useToast();

  const dragAndDropRef = useRef<any>(null);

  // const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  //   setValue(newValue);
  // };

  const removeCubageFields = (
    payload: Record<string, any>,
  ): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(payload).filter(([key]) => !key.startsWith("cubage-")),
    );
  };

  const extractCubage = (data: any) => {
    const extractedData = [];

    for (const key in data) {
      const match = key.match(/cubage-([a-f0-9-]+)-variant-([a-f0-9-]+)/);
      if (match && data[key] === true) {
        extractedData.push({
          cubage: match[1],
          variant: match[2],
        });
      }
    }

    return extractedData;
  };

  const handleProductActivation = async (isActive: boolean) => {
    if (!params.id) return;

    try {
      setLoading(true);

      const result = await updateProductActivationUseCase.handle({
        id: params.id,
        isActive,
      });

      if (result.isFailure) {
        showToast(
          String(
            result.error?.error ??
            result.error?.message ?? 
            'Não foi possível alterar a situação do produto',
          )
          , 'error',);

          return;
      }

      if(isActive) {
        showToast('Produto ativado com sucesso', 'success');
      } else {
        showToast('Produto desativado na Nuvem e HUB. ATENÇÃO: PARA CONCLUIR COMPLETAMENTE A DESATIVAÇÃO, É NECESSÁRIO REALIZAR A AÇÃO MANUALMENTE NO OMIE.', 
          'success');
      }
      
      navigate(productsListUrl);
    } catch (error) {
      console.error('Erro ao alterar situação do produto:', error);
      showToast('Não foi possível alterar a situação do produto', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAction: SubmitHandler<IProductProps> = async values => {
    try {
      setLoading(true);

      let normalizeValues: any = { ...values };

      // validação de cubagem
      const cubages = Array.from(
        new Set(extractCubage(normalizeValues).map((item) => item.variant)),
      );
      if (!cubages?.length) {
        showToast("Selecione pelo menos um cubagem", "error");
        return;
      }

      // validação de variação
      if (!variations?.length) {
        showToast("Selecione pelo menos uma variação", "error");
        return;
      }

      // validação de quantidade
      const quantity = variations.find(
        item => item.variationType === "QUANTIDADE",
      );

      if (!quantity) {
        showToast("Selecione a variação de quantidade", "error");
        return;
      }

      if (normalizeValues?.isResale === true) {
        if (!normalizeValues.omieRawMaterialCode) {
          showToast("Preencha o código OMIE (matéria prima)", "error");
          return;
        }
      }

      // Build quantityScaling from the first variation
      let quantityScaling: { baseQuantity: number; steps: number } | null =
        null;
      if (variations.length > 0) {
        const firstVariation = variations[0];
        const baseQuantity = parseInt(
          firstVariation.values[0].replace("x", ""),
          10,
        );
        const steps = parseInt(firstVariation.howManySteps, 10);
        quantityScaling = { baseQuantity, steps };
      }

      // normalizeValues.maskCode = maskSchema?.generatedMaskName || ''
      // normalizeValues.internalName = maskSchema?.generatedMaskInternalName || ''
      normalizeValues.maskCodeDetails = JSON.stringify(maskSchema);
      normalizeValues.nsStoreIds = [5061090];
      normalizeValues.createOnOmie = true;
      normalizeValues.billingType = billingTypeValue;
      normalizeValues.createOnNSPapello =
        nsStoreValue === "createOnNSPapello" ? true : false;
      normalizeValues.createOnNSEmpo =
        nsStoreValue === "createOnNSEmpo" ? true : false;
      normalizeValues.createOnNuvemShopB2B = values.createOnNuvemShopB2B;
      normalizeValues.createOnNuvemShopB2C = values.createOnNuvemShopB2C;
      normalizeValues.b2bNuvemShopPrice = values.b2bNuvemShopPrice;
      normalizeValues.b2cNuvemShopPrice = values.b2cNuvemShopPrice;
      normalizeValues.productMultiVariants = JSON.stringify({
        B2B: nuvemShopVariationMaskSelections.B2B.map(item => ({
          variantId: item.variantId,
          code: item.code,
          maskCodeDetails: item.maskSchema,
        })),
        B2C: nuvemShopVariationMaskSelections.B2C.map(item => ({
          variantId: item.variantId,
          code: item.code,
          maskCodeDetails: item.maskSchema,
        })),
      });
      normalizeValues = removeCubageFields(normalizeValues);
      normalizeValues.brand = categoriesSelected[0]?.label;

      if (normalizeValues.billingType === "sale") {
        delete normalizeValues.productServiceId;
      }

      const filteredValues = Object.keys(normalizeValues).reduce(
        (acc: Record<string, any>, key) => {
          if (!key.startsWith("switch-")) {
            acc[key] = normalizeValues[key];
          }
          return acc;
        },
        {},
      );

      console.log("filteredValues: ", filteredValues);

      const formData = new FormData();

      Object.keys(normalizeValues).forEach(key => {
        let value = normalizeValues[key];
        if (
          (key === "price" ||
            key === "promotionalPrice" ||
            key === "b2bNuvemShopPrice" ||
            key === "b2cNuvemShopPrice") &&
          value
        ) {
          // Process the price value
          value = value
            .replace(/\s/g, "") // Remove all whitespaces
            .replace(/R\$\s*/g, "") // Remove 'R$' symbol and any trailing spaces
            .replace(/\./g, "") // Remove thousands separator '.'
            .replace(",", "."); // Replace decimal separator ',' with '.'

          // If there's no decimal point, add one after two decimal places
          // Example: "900" becomes "9.00"
          if (!value.includes(".")) {
            const numStr = value.toString();
            if (numStr.length >= 3) {
              value = numStr.slice(0, -2) + "." + numStr.slice(-2);
            } else if (numStr.length === 2) {
              value = "0." + numStr;
            } else if (numStr.length === 1) {
              value = "0.0" + numStr;
            }
          }
        }
        formData.append(key, value);
      });
      console.log("formData: ", formData);

      // Anexar as imagens ao FormData
      const files = dragAndDropRef?.current?.files;
      files.forEach((file: File) => {
        formData.append("images", file);
      });

      if (cubages?.length) {
        cubages.forEach(item => {
          formData.append("cubageVariantsIds[]", JSON.stringify(item));
        });
      }

      // Adicionar políticas de preço ao FormData
      if (selectedPolicies?.length) {
        const normalizePolicies = selectedPolicies.map(item => item.id);
        normalizePolicies.forEach(item => {
          formData.append("pricePoliciesIds[]", JSON.stringify(item));
        });
      }

      // Adicionar quantityScaling ao FormData separadamente para evitar erro de string/objeto no backend
      if (quantityScaling) {
        formData.append(
          "quantityScaling[baseQuantity]",
          quantityScaling.baseQuantity.toString(),
        );
        formData.append(
          "quantityScaling[steps]",
          quantityScaling.steps.toString(),
        );
      }

      // Adicionar nsStore explicitamente se disponível
      if (nsStoreValue) {
        formData.append("nsStore", nsStoreValue);
      }

      formData.forEach((value, key) => {
        console.log(`payload ${key}:`, value);
      });

      const storeResult = params?.id
        ? await updateProductUseCase.handle({ data: formData, id: params?.id })
        : await createProductUseCase.handle(formData);

      if (storeResult.isFailure) {
        showToast(String(storeResult?.error?.error), "error");
        return;
      }

      showToast(
        params?.id ? "Produto Editado" : "Produto Cadastrado",
        "success",
      );

      navigate(productsListUrl);
    } catch (error) {
      console.log("error: ", error);
      // toast({
      //   position: 'top-right',
      //   render: () => <ToastCustom type='fail' title='Erro' description={error.response?.data?.error ?? 'Erro ao logar. Por favor, tente novamente'} />,
      // });
    } finally {
      setLoading(false);
    }
  };

  // const handleSelectCategories = (e: ICategoryEntity) => {
  // const existsCategory = categoriesSelected.find(category => category.id === e.id)

  // if(existsCategory){
  //     const filtered = categoriesSelected?.filter(category => category.id !== e.id)
  //     setCategoriesSelected([...filtered])
  // } else {
  //     setCategoriesSelected(prev => [...prev, e])
  // }
  const handleSelectCategories = (e: IOptionsProps) => {
    const existsCategory = categoriesSelected.find(
      category => category.value === e.value,
    );

    if (existsCategory) {
      const filtered = categoriesSelected?.filter(
        category => category.value !== e.value,
      );
      setCategoriesSelected([...filtered]);
    } else {
      setCategoriesSelected(prev => [...prev, e]);
    }
  };

  const handleListCubages = async () => {
    try {
      setLoading(true);

      const listResult = await listCubagesUseCase.handle(1);

      if (listResult.isFailure) {
        showToast(String(listResult?.error?.error), "error");
        return;
      }

      const list = listResult.getValue();

      if (list?.length) {
        setCubagesList(list);
      }
    } catch (error) {
      console.log("error listCubages function: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleListProductsServices = async () => {
    try {
      setLoading(true);

      const listResult = await listProductsServiceUseCase.handle(1);

      if (listResult.isFailure) {
        showToast(String(listResult?.error?.error), "error");
        return;
      }

      const list = listResult.getValue();

      if (list?.data?.length) {
        const normalize = list.data.map(i => ({
          value: i.id,
          label: i.name,
        }));

        setProductsServicesOptions(normalize);
        setProductsServicesList(list.data);
      }
    } catch (error) {
      console.log("error handleListProductsServices function: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowProduct = async (productId: string) => {
    try {
      setLoading(true);
      setIsDataLoaded(false);

      const showResult = await showProductUseCase.handle(productId);

      if (showResult.isFailure) {
        showToast(String(showResult?.error?.error), "error");
        setIsDataLoaded(true); // Set as loaded even on error to show form
        return;
      }

      const product = showResult.getValue();

      if (product) {
        setData(product);
        console.log("product: ", product);
        console.log(
          "b2bNuvemShopProductId:",
          product?.b2bNuvemShopProductId,
          "-> Boolean:",
          Boolean(product?.b2bNuvemShopProductId),
        );
        console.log(
          "b2cNuvemShopProductId:",
          product?.b2cNuvemShopProductId,
          "-> Boolean:",
          Boolean(product?.b2cNuvemShopProductId),
        );
        console.log(
          "personalized:",
          product.personalized,
          "-> Boolean:",
          Boolean(product.personalized),
        );
        console.log(
          "isResale:",
          product.isResale,
          "-> Boolean:",
          Boolean(product.isResale),
        );

        setValue("name", product.name);
        setValue("description", product.description);
        // Convert price to string and format it
        const priceValue = product.price ? String(product.price) : "0";
        setValue("price", priceValue);
        setValue("code", product.code);
        // setValue('nsStore', product.store === "B2B" ? 'createOnNSEmpo' : 'createOnNSPapello')
        // Garantir que os valores booleanos sejam convertidos corretamente e forçar re-renderização
        setValue(
          "createOnNuvemShopB2B",
          hasPositiveNuvemShopPrice(product?.b2bNuvemShopPrice),
          { shouldDirty: true, shouldTouch: true },
        );
        setValue(
          "createOnNuvemShopB2C",
          hasPositiveNuvemShopPrice(product?.b2cNuvemShopPrice),
          { shouldDirty: true, shouldTouch: true },
        );

        setValue("b2bNuvemShopPrice", product?.b2bNuvemShopPrice || "");
        setValue("b2cNuvemShopPrice", product?.b2cNuvemShopPrice || "");
        setValue("isMultiVariants", Boolean(product.isMultiVariants), {
          shouldDirty: true,
          shouldTouch: true,
        });
        setValue("b2bNuvemShopProductId", product.b2bNuvemShopProductId || "");
        setValue("b2cNuvemShopProductId", product.b2cNuvemShopProductId || "");
        setValue(
          "loadNuvemShopVariationsB2B",
          Boolean(product?.b2bNuvemShopProductId),
          { shouldDirty: true, shouldTouch: true },
        );
        setValue(
          "loadNuvemShopVariationsB2C",
          Boolean(product?.b2cNuvemShopProductId),
          { shouldDirty: true, shouldTouch: true },
        );
        setValue("personalized", Boolean(product.personalized), {
          shouldDirty: true,
          shouldTouch: true,
        });
        setValue("maskCode", product?.maskCodeDetails?.generatedMaskName || "");
        setValue("masterCode", product.masterCode || "");
        setValue(
          "internalName",
          product?.maskCodeDetails?.generatedMaskInternalName || "",
        );

        setValue("isResale", Boolean(product.isResale), {
          shouldDirty: true,
          shouldTouch: true,
        });

        if (product?.approvedArt?.artLink) {
          setValue("artLink", product.approvedArt.artLink);
        }

        setValue(
          "billingType",
          product.billingType === "SERVICE"
            ? "service"
            : product.billingType === "SALES"
            ? "sales"
            : "resale",
        );
        setValue("omieRawMaterialCode", product.omieRawMaterialCode || "");

        // Popular o maskSchema com os dados do backend
        if (product?.maskCodeDetails) {
          setMaskSchema(product?.maskCodeDetails);
        } else {
          setMaskSchema({});
        }

        if (product?.productService?.id) {
          setValue("service", {
            value: String(product.productService?.id),
            label: product.productService?.name,
          });
        }

        product?.cubagesVariantsAssociated?.map(item => {
          setValue(
            `cubage-${item.cubageVariant.cubage?.id}-variant-${item?.cubageVariantId}`,
            true,
          );
        });

        // if(product.createdOnOmie){
        //   setValue('createOnOmie', product.createdOnOmie)
        // }

        if (product?.promotionalPrice) {
          setValue("promotionalPrice", product.promotionalPrice);
        }

        if (product?.ncm) {
          setValue("ncm", product.ncm);
        }

        product?.cubageVariants;

        if (product?.cubagesVariantsAssociated?.length) {
          setCubagesSelected(
            dedupeCubageVariants(product.cubagesVariantsAssociated),
          );
        }

        if (product?.productQuantityScaling) {
          const { baseQuantity, howManySteps } = product.productQuantityScaling;
          setVariations([
            {
              variationType: "QUANTIDADE",
              values: [`x${baseQuantity}`],
              howManySteps: String(howManySteps),
            },
          ]);
        } else if (product?.productVariants?.length) {
          setVariations(
            product.productVariants.map(item => ({
              variationType: item.variantName,
              values: [item.variantValue],
              howManySteps: item.howManySteps || "1",
            })),
          );
        }

        if (product?.productMultiVariants?.length) {
          const toSelection = (
            v: NonNullable<typeof product.productMultiVariants>[0],
          ) => ({
            variantId: Number(v.variantId),
            name: v.maskName || "",
            code: v.code,
            maskSchema: v.maskCodeDetails || {},
          });
          setNuvemShopVariationMaskSelections({
            B2B: product.productMultiVariants
              .filter(v => v.store === "B2B")
              .map(toSelection),
            B2C: product.productMultiVariants
              .filter(v => v.store === "B2C")
              .map(toSelection),
          });
        }

        if (product?.brand) {
          const find = maskOptions.brands.find(
            item => item.label === product.brand,
          );
          console.log("find brand: ", find);
          if (find?.label) {
            setCategoriesSelected([find]);
            setValue(`switch-${find.value}`, true);
          }
        }

        // Set initial selected price policies
        if (product?.pricePolicies?.length) {
          setInitialSelectedPolicies(product.pricePolicies);
          handleSelectPricePolicies(product.pricePolicies);
        }
      }
    } catch (error) {
      console.log("error handleShowProduct function: ", error);
    } finally {
      setLoading(false);
      setIsDataLoaded(true);
    }
  };

  const handleGetMaskProductOptions = async () => {
    try {
      setLoading(true);

      const showResult = await getMaskProductOptionsUseCase.handle();

      if (showResult.isFailure) {
        showToast(String(showResult?.error?.error), "error");
        setMaskOptionsLoaded(true); // Set as loaded even on error
        return;
      }

      const options = showResult.getValue();

      if (options) {
        const { brands } = options;

        setMaskOptions(options);
      }
    } catch (error) {
      console.log("error handleGetMaskProductOptions function: ", error);
    } finally {
      setLoading(false);
      setMaskOptionsLoaded(true);
    }
  };

  const handleLoadNuvemShopVariations = async (store: TStoreType) => {
    const productIdField =
      store === "EMPO" ? "b2bNuvemShopProductId" : "b2cNuvemShopProductId";
    const apiStore: "B2B" | "B2C" = store === "EMPO" ? "B2B" : "B2C";
    const productId = String(getValues(productIdField) ?? "").trim();
    if (!productId) {
      showToast("Informe o ID do produto na NuvemShop.", "error");
      return;
    }
    try {
      setNuvemShopVariationsLoading(store);
      const result = await getVariantsByProductUseCase.handle({
        productId,
        store: apiStore,
      });
      if (result.isFailure) {
        showToast(String(result?.error?.error ?? result?.error), "error");
        return;
      }
      const rows = result.getValue();
      if (!rows) {
        showToast("Não foi possível carregar as variações.", "error");
        return;
      }
      if (store === "EMPO") {
        setNuvemShopVariantsB2B(rows);
      } else {
        setNuvemShopVariantsB2C(rows);
      }
      setNuvemShopVariationsModalChannel(apiStore);
      setShowNuvemShopVariationsModal(true);
    } catch (error: any) {
      console.log("error handleLoadNuvemShopVariations: ", error);
      showToast("Erro ao carregar variações.", "error");
    } finally {
      setNuvemShopVariationsLoading(null);
    }
  };

  const handleRemoveTemporary = (cubageVariant: ICubageVariantAssociated) => {
    const variantId = cubageVariant.cubageVariantId;

    setCubagesSelected((prev) =>
      prev.filter((item) => item.cubageVariantId !== variantId),
    );
    setValue(
      `cubage-${cubageVariant?.cubageVariant?.cubage?.id}-variant-${variantId}`,
      false,
    );
  };

  const listCategories = async () => {
    try {
      setLoading(true);

      const listResult = await listCategoriesUseCase.handle({
        page: 1,
        getAll: true,
      });

      if (listResult.isFailure) {
        showToast(String(listResult?.error?.error), "error");
        return;
      }

      const list = listResult.getValue();

      //  if(list){
      //     setCategories(list.data)

      //  }
    } catch (error) {
      console.log("error listCategories function: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemporary = (item: ICubageVariantAssociated) => {
    setCubagesSelected((prev) => {
      const variantId = item.cubageVariantId || item.cubageVariant?.id;
      if (!variantId || prev.some((current) => current.cubageVariantId === variantId)) {
        return prev;
      }
      return [...prev, item];
    });

    setValue(
      `cubage-${item?.cubageVariant?.cubage?.id}-variant-${item.cubageVariantId}`,
      true,
    );
  };

  const handlePricePoliciesChange = (
    policies: Array<{
      id: string;
      minQuantity: number;
      discountPercentage: number;
    }>,
  ) => {
    // setPricePolicies(policies);
  };

  const handleAddVariation = (
    variationType: string,
    values: string[],
    howManySteps: string,
  ) => {
    const existingVariationIndex = variations.findIndex(
      v => v.variationType === variationType,
    );

    if (existingVariationIndex >= 0) {
      // Update existing variation
      const updatedVariations = [...variations];
      updatedVariations[existingVariationIndex] = {
        variationType,
        values,
        howManySteps,
      };
      setVariations(updatedVariations);
    } else {
      // Add new variation
      setVariations(prev => [...prev, { variationType, values, howManySteps }]);
    }
  };

  const handleRemoveVariation = (variationType: string) => {
    setVariations(prev => prev.filter(v => v.variationType !== variationType));
  };

  const handleRemoveValue = (variationType: string, value: string) => {
    setVariations(
      prev =>
        prev
          .map(variation => {
            if (variation.variationType === variationType) {
              const updatedValues = variation.values.filter(v => v !== value);

              // Se não restaram valores, remove a variação inteira
              if (updatedValues.length === 0) {
                return null;
              }

              return { ...variation, values: updatedValues };
            }
            return variation;
          })
          .filter(Boolean) as Array<{
          variationType: string;
          values: string[];
          howManySteps: string;
        }>,
    );
  };

  // Primeiro carrega as opções de máscara (brands, etc)
  useEffect(() => {
    handleGetMaskProductOptions();
  }, []);

  // Depois que as brands forem carregadas, carrega o produto (se houver ID)
  useEffect(() => {
    if (maskOptionsLoaded) {
      if (params.id) {
        handleShowProduct(params.id);
      } else {
        // Se não há ID, estamos no modo de criação, então os dados estão "carregados"
        setIsDataLoaded(true);
      }
    }
  }, [params, maskOptionsLoaded]);

  // useEffect(() => {
  //   if(nsStoreValue){
  //     setCategoriesSelected([])
  //     const values = getValues()
  //     console.log("values: ", values)
  //     const swtichValues = FindKeyWithPrefix(values, 'swtich-')

  //     swtichValues.forEach((item: { key: string; value: boolean} )=> {
  //       unregister(item.key as keyof IProductProps)
  //     })

  //   }
  // }, [nsStoreValue])

  const closeNuvemShopVariationsModal = () => {
    setShowNuvemShopVariationsModal(false);
    setNuvemShopVariationsModalChannel(null);
  };

  const handleConfirmNuvemShopVariationMasks = (
    channel: "B2B" | "B2C",
    selections: INuvemShopVariationMaskSelection[],
  ) => {
    setNuvemShopVariationMaskSelections(prev => ({
      ...prev,
      [channel]: selections,
    }));
    closeNuvemShopVariationsModal();
  };

  return {
    loading,
    isDataLoaded,
    handleSubmit,
    handleAction,
    register,
    errors,
    control,
    watch,
    handleProductActivation,
    handleSelectCategories,

    showCategoriesModal,
    setShowCategoriesModal,
    showSEOModal,
    setShowSEOModal,
    showCubageModal,
    setShowCubageModal,
    showInternalCode,
    setShowInternalCode,
    showVariationsModal,
    setShowVariationsModal,
    maskSchema,
    setMaskSchema,
    variations,
    setVariations,
    images,
    setImages,
    cubagesList,
    handleListCubages,
    categories,
    setCategories,
    dragAndDropRef,
    handleListProductsServices,
    billingTypeValue,
    productsServicesOptions,
    cubagesSelected,
    handleRemoveTemporary,
    handleAddTemporary,
    storeTypeOptions,
    nsStoreValue,
    listCategories,
    categoriesSelected,
    data,
    setCategoriesSelected,

    handlePricePoliciesChange,
    handleAddVariation,
    handleRemoveVariation,
    handleRemoveValue,
    showPricePoliciesModal,
    openPricePoliciesModal,
    closePricePoliciesModal,
    handleSelectPricePolicies,
    selectedPolicies,
    removePolicy,

    maskOptions,
    params,
    showNuvemShopVariationsModal,
    nuvemShopVariationsModalChannel,
    closeNuvemShopVariationsModal,
    nuvemShopVariantsB2B,
    nuvemShopVariantsB2C,
    nuvemShopVariationsLoading,
    handleLoadNuvemShopVariations,
    nuvemShopVariationMaskSelections,
    handleConfirmNuvemShopVariationMasks,
    productsListUrl,
  };
}
